import React, { useState } from 'react';
import Icon from '../components/Icon';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import AgentCreationModal from '../components/AgentCreationModal';
import agentService from '../services/agentService';
import workflowService from '../services/workflowService';

const ICPContainer = styled.div`
  padding: 0;
  max-width: 1000px;
  margin: 0 auto;
`;

const HeaderSection = styled(motion.div)`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-family: var(--font-heading);
  font-size: var(--text-3xl);
  font-weight: 700;
  margin-bottom: 1rem;
  color: var(--text-primary);
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1.2rem;
  line-height: 1.6;
  max-width: 700px;
  margin: 0 auto;
`;

const FilterCard = styled(motion.div)`
  background: var(--gradient-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-xl);
  padding: 3rem;
  backdrop-filter: blur(20px);
  box-shadow: var(--shadow-lg);
  transition: var(--transition-normal);

  &:hover {
    border-color: var(--accent-cyan);
    box-shadow: var(--shadow-glow);
  }

  @media (max-width: 768px) {
    padding: 2rem;
    margin: 1rem;
  }
`;

const FilterTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: 1.8rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const FilterDescription = styled.p`
  color: var(--text-secondary);
  margin-bottom: 2.5rem;
  line-height: 1.6;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FilterGroup = styled.div`
  background: rgba(22, 26, 43, 0.5);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  transition: var(--transition-normal);

  &:hover {
    border-color: var(--accent-cyan);
    background: rgba(22, 26, 43, 0.7);
  }
`;

const FilterLabel = styled.div`
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// Explains how a filter is actually applied when Apollo cannot match it exactly.
const FilterHint = styled.div`
  color: var(--text-secondary);
  font-size: 0.8rem;
  font-weight: 400;
  margin-top: -0.75rem;
  margin-bottom: 0.75rem;
  line-height: 1.4;
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownHeader = styled.div`
  background: rgba(22, 26, 43, 0.8);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 0.75rem;
  cursor: pointer;
  transition: var(--transition-normal);
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    border-color: var(--accent-cyan);
  }

  &.open {
    border-color: var(--accent-cyan);
    box-shadow: var(--shadow-glow);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

const DropdownArrow = styled.span`
  color: var(--text-muted);
  transition: var(--transition-fast);
  transform: ${props => props.open ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const DropdownContent = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(22, 26, 43, 0.95);
  border: 1px solid var(--accent-cyan);
  border-top: none;
  border-radius: 0 0 var(--radius-md) var(--radius-md);
  backdrop-filter: blur(20px);
  z-index: 1000;
  max-height: 300px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SearchInput = styled.input`
  background: rgba(22, 26, 43, 0.9);
  border: none;
  border-bottom: 1px solid var(--border-color);
  padding: 0.75rem;
  color: var(--text-primary);
  font-family: var(--font-primary);
  outline: none;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    background: rgba(22, 26, 43, 1);
  }
`;

const OptionsList = styled.div`
  overflow-y: auto;
  max-height: 200px;
`;

const OptionItem = styled.div`
  padding: 0.75rem;
  cursor: pointer;
  transition: var(--transition-fast);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-secondary);

  &:hover {
    background: rgba(34, 211, 238, 0.1);
    color: var(--text-primary);
  }

  &.selected {
    background: rgba(34, 211, 238, 0.2);
    color: var(--accent-cyan);
  }

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: var(--accent-cyan);
  }
`;

const SelectedTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const Tag = styled.span`
  background: var(--accent-cyan);
  color: var(--primary-bg);
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .remove {
    cursor: pointer;
    font-weight: 700;
    
    &:hover {
      color: var(--error-color);
    }
  }
`;

const RangeInputGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;
`;

const RangeInput = styled.input`
  background: rgba(22, 26, 43, 0.8);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 0.75rem;
  color: var(--text-primary);
  font-size: 1rem;
  transition: var(--transition-normal);
  font-family: var(--font-primary);

  &:focus {
    outline: none;
    border-color: var(--accent-cyan);
    box-shadow: var(--shadow-glow);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const RangeSeparator = styled.span`
  color: var(--text-muted);
  font-weight: 600;
`;

const RevenueOptions = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const RevenueOption = styled.button`
  background: ${props => props.$active ? 'var(--accent-cyan)' : 'rgba(22, 26, 43, 0.8)'};
  color: ${props => props.$active ? 'var(--primary-bg)' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.$active ? 'var(--accent-cyan)' : 'var(--border-color)'};
  border-radius: var(--radius-md);
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: var(--transition-normal);
  font-family: var(--font-primary);

  &:hover {
    border-color: var(--accent-cyan);
    color: var(--accent-cyan);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid var(--border-color);

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Button = styled(motion.button)`
  font-family: var(--font-primary);
  font-weight: 600;
  padding: 1rem 2rem;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition-normal);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.95rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 140px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.primary {
    background: var(--accent-cyan);
    color: var(--primary-bg);
    box-shadow: var(--shadow-md);

    &:hover:not(:disabled) {      box-shadow: var(--shadow-lg);
    }
  }

  &.secondary {
    background: transparent;
    color: var(--text-secondary);
    border: 2px solid var(--border-color);

    &:hover:not(:disabled) {
      color: var(--accent-cyan);
      border-color: var(--accent-cyan);
      box-shadow: var(--shadow-sm);
    }
  }
`;

// Error Message Styled Component
const ErrorMessage = styled(motion.div)`
  color: var(--error-color);
  background: rgba(248, 113, 113, 0.1);
  border: 1px solid var(--error-color);
  padding: 1rem;
  border-radius: var(--radius-md);
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 500;
`;

// Async MultiSelect Component with API Lookup
// Constant for stable reference
const EMPTY_ARRAY = [];

const AsyncMultiSelect = ({
  label,
  helperText,
  placeholder,
  apiType, // 'job_title', 'company', etc.
  staticOptions = EMPTY_ARRAY, // Stable default
  selectedItems,
  setSelectedItems,
  dropdownKey,
  dropdownStates,
  toggleDropdown,
  toggleSelection,
  removeTag
}) => {
  const isOpen = dropdownStates[dropdownKey];
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState(staticOptions);
  const [isSearching, setIsSearching] = useState(false);

  // Debounce search - but also show user's input immediately
  React.useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchTerm.length >= 1 && apiType) {
        setIsSearching(true);
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`/api/agents/lookup?type=${apiType}&q=${encodeURIComponent(searchTerm)}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              // Ensure user's exact input is always first option
              const results = data.data || [];
              if (!results.includes(searchTerm.trim()) && searchTerm.trim()) {
                setSuggestions([searchTerm.trim(), ...results]);
              } else {
                setSuggestions(results);
              }
            }
          }
        } catch (err) {
          console.error('Search error:', err);
          // On error, still show user's input as option
          if (searchTerm.trim()) {
            setSuggestions([searchTerm.trim()]);
          }
        } finally {
          setIsSearching(false);
        }
      } else if (searchTerm.length === 0) {
        setSuggestions(staticOptions);
      } else if (searchTerm.trim()) {
        // For single character, show user's input
        setSuggestions([searchTerm.trim(), ...staticOptions]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, apiType, staticOptions]);

  // Handle free text entry (pressing Enter)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      e.preventDefault();
      // Add custom value if not already selected
      if (!selectedItems.includes(searchTerm.trim())) {
        toggleSelection(selectedItems, setSelectedItems, searchTerm.trim());
        setSearchTerm('');
      }
    }
  };

  return (
    <FilterGroup>
      <FilterLabel>{label}</FilterLabel>
      {helperText && <FilterHint>{helperText}</FilterHint>}

      <DropdownContainer>
        <DropdownHeader
          className={isOpen ? 'open' : ''}
          onClick={() => toggleDropdown(dropdownKey)}
        >
          <span style={{ color: selectedItems.length > 0 ? 'var(--text-primary)' : 'var(--text-muted)' }}>
            {selectedItems.length > 0 ? `${selectedItems.length} selected` : placeholder}
          </span>
          <DropdownArrow open={isOpen}><Icon name="chevronDown" size={14} /></DropdownArrow>
        </DropdownHeader>

        {isOpen && (
          <DropdownContent>
            <SearchInput
              placeholder={`Search ${label.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
            <OptionsList>
              {isSearching && <OptionItem>Searching...</OptionItem>}
              {!isSearching && suggestions.length === 0 && <OptionItem>No suggestions. Press Enter to add "{searchTerm}"</OptionItem>}

              {!isSearching && suggestions.map((option) => (
                <OptionItem
                  key={option}
                  className={selectedItems.includes(option) ? 'selected' : ''}
                  onClick={() => toggleSelection(selectedItems, setSelectedItems, option)}
                >
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(option)}
                    onChange={() => { }}
                    onClick={(e) => e.stopPropagation()} // Prevent double toggle
                  />
                  {option}
                </OptionItem>
              ))}
            </OptionsList>
          </DropdownContent>
        )}
      </DropdownContainer>

      {selectedItems.length > 0 && (
        <SelectedTags>
          {selectedItems.map((item) => (
            <Tag key={item}>
              {item}
              <span
                className="remove"
                onClick={() => removeTag(selectedItems, setSelectedItems, item)}
              >
                ×
              </span>
            </Tag>
          ))}
        </SelectedTags>
      )}
    </FilterGroup>
  );
};

const ICPSelection = () => {
  const navigate = useNavigate();
  const [jobTitles, setJobTitles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [revenueType, setRevenueType] = useState('is between');
  const [revenueMin, setRevenueMin] = useState('');
  const [revenueMax, setRevenueMax] = useState('');
  const [employeeRange, setEmployeeRange] = useState([]);
  const [fundingStages, setFundingStages] = useState([]);
  const [companyTypes, setCompanyTypes] = useState([]);
  const [error, setError] = useState('');
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Tab states for job titles
  const [jobTitlesTab, setJobTitlesTab] = useState('simple');
  const [includeSimilarTitles, setIncludeSimilarTitles] = useState(false);

  // --- New Fields State ---
  const [seniority, setSeniority] = useState([]);
  const [jobFunction, setJobFunction] = useState([]);
  const [emailStatus, setEmailStatus] = useState([]);
  const [industry, setIndustry] = useState([]);
  const [estimatedNumEmployees, setEstimatedNumEmployees] = useState({ min: '', max: '' });
  const [lastFundingDate, setLastFundingDate] = useState({ min: '', max: '' });


  // Newest Fields
  const [technologies, setTechnologies] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [foundedYear, setFoundedYear] = useState({ min: '', max: '' });
  const [fundingAmount, setFundingAmount] = useState({ min: '', max: '' });


  // When a campaign is named in the URL this page edits that campaign's
  // audience instead of the project's. Two campaigns for one product can then
  // chase entirely different people over one shared ledger.
  const [search] = useSearchParams();
  const campaignId = search.get('campaign');
  const [campaignName, setCampaignName] = useState('');
  const [inherited, setInherited] = useState(false);

  // Dropdown states updated
  const [dropdownStates, setDropdownStates] = useState({
    jobTitles: false,
    companies: false,
    locations: false,
    funding: false,
    seniority: false,
    jobFunction: false,
    emailStatus: false,
    industry: false,
    technologies: false,
    keywords: false,
    companyTypes: false,
    employeeRange: false
  });

  // Modal state
  const [showModal, setShowModal] = useState(false);

  // Check if we're in update mode and load existing agent data
  React.useEffect(() => {
    const loadAgentData = async () => {
      try {
        setIsLoading(true);
        const agentId = sessionStorage.getItem('currentAgentId');
        const updateMode = sessionStorage.getItem('isUpdateMode') === 'true';

        setIsUpdateMode(updateMode);

        // A campaign that has never been given its own audience starts from the
        // project's, so opening this page shows what the campaign actually runs
        // against today rather than an empty form.
        let apollo = null;

        if (campaignId) {
          const { data } = await workflowService.getWorkflow(campaignId);
          const workflow = data.workflow;
          setCampaignName(workflow.name || '');

          const node = (workflow.nodes || []).find((n) => n.type === 'agent');
          const own = node && node.config && node.config.icp;

          if (own && Object.keys(own).length) {
            apollo = own;
          } else if (agentId) {
            const response = await agentService.getAgentById(agentId);
            apollo = response.data.agent.apollo || null;
            setInherited(true);
          }
        } else if (updateMode && agentId) {
          const response = await agentService.getAgentById(agentId);
          apollo = response.data.agent.apollo || null;
        }

        if (apollo) {

            if (apollo.jobTitles) setJobTitles(apollo.jobTitles);
            if (apollo.companies) setCompanies(apollo.companies);
            if (apollo.locations) setLocations(apollo.locations);
            if (apollo.revenue?.type) setRevenueType(apollo.revenue.type);
            if (apollo.revenue?.min) setRevenueMin(apollo.revenue.min.toString());
            if (apollo.revenue?.max) setRevenueMax(apollo.revenue.max.toString());
            if (apollo.employeeRange) setEmployeeRange(Array.isArray(apollo.employeeRange) ? apollo.employeeRange : []);
            if (apollo.fundingStages) setFundingStages(apollo.fundingStages);
            if (apollo.companyTypes) setCompanyTypes(Array.isArray(apollo.companyTypes) ? apollo.companyTypes : []);
            if (apollo.jobTitleSettings?.tab) setJobTitlesTab(apollo.jobTitleSettings.tab);
            if (apollo.jobTitleSettings?.includeSimilarTitles !== undefined) {
              setIncludeSimilarTitles(apollo.jobTitleSettings.includeSimilarTitles);
            }
            // NEW FIELDS LOADING
            if (apollo.seniority) setSeniority(apollo.seniority);
            if (apollo.jobFunction) setJobFunction(apollo.jobFunction);
            if (apollo.emailStatus) setEmailStatus(apollo.emailStatus);
            if (apollo.industry) setIndustry(apollo.industry);
            if (apollo.estimatedNumEmployees) setEstimatedNumEmployees(apollo.estimatedNumEmployees);
            if (apollo.lastFundingDate) setLastFundingDate(apollo.lastFundingDate);

            // Newest Fields Loading
            if (apollo.technologies) setTechnologies(apollo.technologies);
            if (apollo.keywords) setKeywords(apollo.keywords);
            if (apollo.foundedYear) setFoundedYear(apollo.foundedYear);
            if (apollo.fundingAmount) setFundingAmount(apollo.fundingAmount);
        }
      } catch (error) {
        console.error('Error loading agent data:', error);
        setError('Failed to load agent data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadAgentData();
  }, [campaignId]);

  /**
   * Writes this audience onto the campaign's agent node instead of the project.
   *
   * Targeting belongs to the campaign now: two campaigns for one product can
   * chase entirely different people, and a project-wide audience made that
   * impossible. The project keeps its own audience as the fallback for every
   * campaign that has not been given one.
   */
  const saveToCampaign = async () => {
    const { data } = await workflowService.getWorkflow(campaignId);
    const workflow = data.workflow;
    const nodes = (workflow.nodes || []).map((node) => (
      node.type === 'agent'
        ? { ...node, config: { ...(node.config || {}), icp: campaignTargeting() } }
        : node
    ));

    if (!nodes.some((node) => node.type === 'agent')) {
      throw new Error('This campaign has no sourcing step, so there is nobody to target.');
    }

    await workflowService.updateWorkflow(campaignId, { nodes });
  };

  const handleBack = () => { navigate(campaignId ? '/workflow' : '/product-description'); };

  /**
   * The same filters in the shape a campaign node stores.
   *
   * The project endpoint takes revenue as three flat fields and repacks them;
   * a node config is written straight through, so it has to arrive already
   * shaped the way the sourcing path reads it.
   */
  const campaignTargeting = () => {
    const form = collectICP();
    const { revenueType, revenueMin, revenueMax, jobTitlesTab, includeSimilarTitles, ...rest } = form;

    return {
      ...rest,
      revenue: (revenueMin || revenueMax)
        ? { type: revenueType, min: revenueMin, max: revenueMax }
        : undefined
    };
  };

  /** Everything on this form, in the shape the project endpoint accepts. */
  const collectICP = () => {
    return {
        jobTitles,
        companies,
        locations,
        revenueType,
        revenueMin: revenueMin ? parseFloat(revenueMin) : undefined,
        revenueMax: revenueMax ? parseFloat(revenueMax) : undefined,
        employeeRange: employeeRange || undefined,
        fundingStages,
        companyTypes,
        jobTitlesTab,
        includeSimilarTitles,
        // New Fields Payload
        seniority,
        jobFunction,
        emailStatus,
        industry,
        estimatedNumEmployees: (estimatedNumEmployees.min || estimatedNumEmployees.max) ? {
          min: estimatedNumEmployees.min ? parseFloat(estimatedNumEmployees.min) : undefined,
          max: estimatedNumEmployees.max ? parseFloat(estimatedNumEmployees.max) : undefined
        } : undefined,
        lastFundingDate: (lastFundingDate.min || lastFundingDate.max) ? lastFundingDate : undefined,

        // Newest Fields Payload
        technologies,
        keywords,
        foundedYear: (foundedYear.min || foundedYear.max) ? {
          min: foundedYear.min ? parseInt(foundedYear.min) : undefined,
          max: foundedYear.max ? parseInt(foundedYear.max) : undefined
        } : undefined,
        fundingAmount: (fundingAmount.min || fundingAmount.max) ? {
          min: fundingAmount.min ? parseFloat(fundingAmount.min) : undefined,
          max: fundingAmount.max ? parseFloat(fundingAmount.max) : undefined
        } : undefined
    };
  };

  const handleNext = async () => {
    setError('');

    try {
      if (campaignId) {
        await saveToCampaign();
        navigate('/workflow');
        return;
      }

      const agentId = sessionStorage.getItem('currentAgentId');
      if (!agentId) {
        throw new Error('No project is selected. Open one from the switcher and try again.');
      }

      await agentService.updateAgentICP(agentId, collectICP());

      // Only activate the agent if it's a new creation, not an update
      if (!isUpdateMode) {
        await agentService.activateAgent(agentId);
      }

      setShowModal(true);
    } catch (error) {
      console.error('Error saving the audience:', error);
      setError(error.message || 'Could not save this audience. Please try again.');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    sessionStorage.removeItem('currentAgentId');
    sessionStorage.removeItem('isUpdateMode');
    navigate('/agent');
  };


  const toggleDropdown = (dropdown) => { setDropdownStates(prev => ({ ...prev, [dropdown]: !prev[dropdown] })); };

  // Helper for generic toggle
  const toggleSelection = (list, setList, item) => {
    if (list.includes(item)) setList(list.filter(i => i !== item));
    else setList([...list, item]);
  };

  const removeTag = (list, setList, value) => { setList(list.filter(item => item !== value)); };




  if (isLoading) {
    return (
      <ICPContainer>
        <HeaderSection initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Title>Loading...</Title>
          <Subtitle>Loading agent data...</Subtitle>
        </HeaderSection>
      </ICPContainer>
    );
  }

  return (
    <ICPContainer>
      <HeaderSection initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <Title>{campaignId ? 'Who this campaign goes after' : (isUpdateMode ? 'Update your audience' : 'Who to go after')}</Title>
        <Subtitle>
          {campaignId
            ? (inherited
              ? `${campaignName || 'This campaign'} is using the project's audience. Change anything here and it becomes this campaign's own, leaving every other campaign alone.`
              : `The people ${campaignName || 'this campaign'} looks for. Other campaigns in this project are unaffected.`)
            : 'The default audience for this project. A campaign can override it with its own.'}
        </Subtitle>
      </HeaderSection>

      <FilterCard initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
        <FilterTitle>{isUpdateMode ? ' Update Target Filters' : ' Target Filters'}</FilterTitle>
        <FilterDescription>Use the dynamic search filters below to precisely target your ideal prospects.</FilterDescription>

        {error && <ErrorMessage initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>{error}</ErrorMessage>}

        <FiltersGrid>
          {/* Job Titles (Async) */}
          <AsyncMultiSelect
            label=" Job Titles"
            placeholder="Search job titles (e.g. CEO, VP Sales)"
            apiType="job_title"
            selectedItems={jobTitles}
            setSelectedItems={setJobTitles}
            dropdownKey="jobTitles"
            dropdownStates={dropdownStates}
            toggleDropdown={toggleDropdown}
            toggleSelection={toggleSelection}
            removeTag={removeTag}
          />

          {/* Seniority (Async) */}
          <AsyncMultiSelect
            label=" Seniority"
            placeholder="Search seniority (e.g. C-Suite, Director)"
            apiType="seniority"
            selectedItems={seniority}
            setSelectedItems={setSeniority}
            dropdownKey="seniority"
            dropdownStates={dropdownStates}
            toggleDropdown={toggleDropdown}
            toggleSelection={toggleSelection}
            removeTag={removeTag}
          />

          {/* Job Function (Async) */}
          <AsyncMultiSelect
            label=" Job Function"
            helperText="Matched as job title keywords — Apollo has no separate job function filter."
            placeholder="Search function (e.g. Sales, Engineering)"
            apiType="job_function"
            selectedItems={jobFunction}
            setSelectedItems={setJobFunction}
            dropdownKey="jobFunction"
            dropdownStates={dropdownStates}
            toggleDropdown={toggleDropdown}
            toggleSelection={toggleSelection}
            removeTag={removeTag}
          />

          {/* Company (Async) */}
          <AsyncMultiSelect
            label=" Company"
            placeholder="Search companies"
            apiType="company"
            selectedItems={companies}
            setSelectedItems={setCompanies}
            dropdownKey="companies"
            dropdownStates={dropdownStates}
            toggleDropdown={toggleDropdown}
            toggleSelection={toggleSelection}
            removeTag={removeTag}
          />

          {/* Industry (Async) */}
          <AsyncMultiSelect
            label=" Industry Keywords"
            helperText="Matched as company keywords — Apollo's exact industry filter requires internal tag IDs."
            placeholder="Search industries"
            apiType="industry"
            selectedItems={industry}
            setSelectedItems={setIndustry}
            dropdownKey="industry"
            dropdownStates={dropdownStates}
            toggleDropdown={toggleDropdown}
            toggleSelection={toggleSelection}
            removeTag={removeTag}
          />

          {/* Location (Async) */}
          <AsyncMultiSelect
            label=" Location"
            placeholder="Search locations"
            apiType="location"
            selectedItems={locations}
            setSelectedItems={setLocations}
            dropdownKey="locations"
            dropdownStates={dropdownStates}
            toggleDropdown={toggleDropdown}
            toggleSelection={toggleSelection}
            removeTag={removeTag}
          />

          {/* Technologies (Async) */}
          <AsyncMultiSelect
            label=" Technologies"
            placeholder="Search technologies (e.g. Salesforce)"
            apiType="technology"
            selectedItems={technologies}
            setSelectedItems={setTechnologies}
            dropdownKey="technologies"
            dropdownStates={dropdownStates}
            toggleDropdown={toggleDropdown}
            toggleSelection={toggleSelection}
            removeTag={removeTag}
          />

          {/* Keywords (Async) */}
          <AsyncMultiSelect
            label=" Keywords"
            placeholder="Search keywords (e.g. B2B, SaaS)"
            apiType="keyword"
            selectedItems={keywords}
            setSelectedItems={setKeywords}
            dropdownKey="keywords"
            dropdownStates={dropdownStates}
            toggleDropdown={toggleDropdown}
            toggleSelection={toggleSelection}
            removeTag={removeTag}
          />

          {/* Company Size Sections */}


          <AsyncMultiSelect
            label=" Employee Range"
            placeholder="Search range (e.g. 1-10, 500+)"
            apiType="employee_range"
            staticOptions={['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5001+']}
            selectedItems={employeeRange}
            setSelectedItems={setEmployeeRange}
            dropdownKey="employeeRange"
            dropdownStates={dropdownStates}
            toggleDropdown={toggleDropdown}
            toggleSelection={toggleSelection}
            removeTag={removeTag}
          />

          <FilterGroup>
            <FilterLabel>Est. Employee Count (Exact)</FilterLabel>
            <RangeInputGroup>
              <RangeInput type="number" placeholder="Min" value={estimatedNumEmployees.min} onChange={(e) => setEstimatedNumEmployees({ ...estimatedNumEmployees, min: e.target.value })} />
              <RangeSeparator>-</RangeSeparator>
              <RangeInput type="number" placeholder="Max" value={estimatedNumEmployees.max} onChange={(e) => setEstimatedNumEmployees({ ...estimatedNumEmployees, max: e.target.value })} />
            </RangeInputGroup>
          </FilterGroup>

          {/* Financials */}
          <FilterGroup>
            <FilterLabel>Revenue</FilterLabel>
            <RevenueOptions>
              {['is between', 'is known', 'is unknown'].map((opt) => (
                <RevenueOption key={opt} $active={revenueType === opt} onClick={() => setRevenueType(opt)}>{opt}</RevenueOption>
              ))}
            </RevenueOptions>
            {revenueType === 'is between' && (
              <RangeInputGroup>
                <RangeInput type="number" placeholder="Min" value={revenueMin} onChange={(e) => setRevenueMin(e.target.value)} />
                <RangeSeparator>to</RangeSeparator>
                <RangeInput type="number" placeholder="Max" value={revenueMax} onChange={(e) => setRevenueMax(e.target.value)} />
              </RangeInputGroup>
            )}

            <div style={{ marginTop: '2rem' }}></div>

            <AsyncMultiSelect
              label=" Company Type"
              placeholder="Search type (e.g. Public, SaaS)"
              apiType="company_type"
              staticOptions={['Private', 'Public', 'Startup', 'SMB', 'Enterprise', 'Non-Profit', 'Government']}
              selectedItems={companyTypes}
              setSelectedItems={setCompanyTypes}
              dropdownKey="companyTypes"
              dropdownStates={dropdownStates}
              toggleDropdown={toggleDropdown}
              toggleSelection={toggleSelection}
              removeTag={removeTag}
            />
          </FilterGroup>

          {/* Funding */}
          <AsyncMultiSelect
            label=" Funding Stages"
            placeholder="Search funding stages (e.g. Series A, Seed)"
            apiType="funding_stage"
            selectedItems={fundingStages}
            setSelectedItems={setFundingStages}
            dropdownKey="funding"
            dropdownStates={dropdownStates}
            toggleDropdown={toggleDropdown}
            toggleSelection={toggleSelection}
            removeTag={removeTag}
          />

          <FilterGroup>
            <FilterLabel>Total Funding Amount</FilterLabel>
            <RangeInputGroup>
              <RangeInput type="number" placeholder="Min" value={fundingAmount.min} onChange={(e) => setFundingAmount({ ...fundingAmount, min: e.target.value })} />
              <RangeSeparator>to</RangeSeparator>
              <RangeInput type="number" placeholder="Max" value={fundingAmount.max} onChange={(e) => setFundingAmount({ ...fundingAmount, max: e.target.value })} />
            </RangeInputGroup>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Last Funding Date</FilterLabel>
            <RangeInputGroup>
              <RangeInput type="date" value={lastFundingDate.min} onChange={(e) => setLastFundingDate({ ...lastFundingDate, min: e.target.value })} />
              <RangeSeparator>to</RangeSeparator>
              <RangeInput type="date" value={lastFundingDate.max} onChange={(e) => setLastFundingDate({ ...lastFundingDate, max: e.target.value })} />
            </RangeInputGroup>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Founded Year</FilterLabel>
            <RangeInputGroup>
              <RangeInput type="number" placeholder="Year From" value={foundedYear.min} onChange={(e) => setFoundedYear({ ...foundedYear, min: e.target.value })} />
              <RangeSeparator>-</RangeSeparator>
              <RangeInput type="number" placeholder="Year To" value={foundedYear.max} onChange={(e) => setFoundedYear({ ...foundedYear, max: e.target.value })} />
            </RangeInputGroup>
          </FilterGroup>

          {/* Email Status */}
          <AsyncMultiSelect
            label=" Email Status"
            placeholder="Search email status (e.g. Verified)"
            apiType="email_status"
            selectedItems={emailStatus}
            setSelectedItems={setEmailStatus}
            dropdownKey="emailStatus"
            dropdownStates={dropdownStates}
            toggleDropdown={toggleDropdown}
            toggleSelection={toggleSelection}
            removeTag={removeTag}
          />
        </FiltersGrid>

        <ButtonGroup>
          <Button type="button" className="secondary" onClick={handleBack}>Back</Button>
          <Button type="button" className="primary" onClick={handleNext}>
            {campaignId ? 'Save this audience' : (isUpdateMode ? 'Save' : 'Next step')}
          </Button>
        </ButtonGroup>
      </FilterCard>

      <AgentCreationModal isOpen={showModal} onClose={handleModalClose} isUpdateMode={isUpdateMode} />
    </ICPContainer>
  );
};

export default ICPSelection;
